import { base64UrlToBytes, bytesToBase64Url, bytesToUtf8, utf8ToBytes } from './encoding';
import { decryptAesGcm, derivePbkdf2KeyBytes, encryptAesGcm, xorBytes } from './crypto';

export const PASTE_LINK_MAX_BYTES = 32 * 1024;
export const PASTE_PBKDF2_ITERATIONS = 200_000;
export const PASTE_KEY_BYTES = 32;
export const PASTE_SALT_BYTES = 16;

export type PasteKdf =
  | {
      name: 'raw';
    }
  | {
      name: 'PBKDF2';
      hash: 'SHA-256';
      iterations: number;
      combiner: 'xor';
    };

export type PasteCipher = {
  version: 1;
  ciphertext: string;
  iv: string;
  salt: string;
  kdf: PasteKdf;
  alg: 'AES-GCM';
  contentType: string;
  size: number;
};

export type PasteLinkPayload = {
  version: 1;
  key: string;
  cipher: PasteCipher;
  createdAt: string;
};

export function encodePasteLinkPayload(payload: PasteLinkPayload): string {
  return bytesToBase64Url(utf8ToBytes(JSON.stringify(payload)));
}

export function decodePasteLinkPayload(fragment: string): {
  keyBytes: Uint8Array;
  payload: PasteLinkPayload;
} {
  const payload = JSON.parse(bytesToUtf8(base64UrlToBytes(fragment))) as PasteLinkPayload;
  if (payload.version !== 1) {
    throw new Error('Unsupported paste payload.');
  }
  const keyBytes = base64UrlToBytes(payload.key);
  if (keyBytes.length !== PASTE_KEY_BYTES) {
    throw new Error('Invalid paste key length.');
  }
  return { keyBytes, payload };
}

export async function derivePasteKeyBytes(options: {
  keyBytes: Uint8Array;
  passphrase?: string;
  salt: Uint8Array;
  iterations?: number;
}): Promise<{ keyBytes: Uint8Array; kdf: PasteKdf }> {
  const { keyBytes, passphrase, salt, iterations = PASTE_PBKDF2_ITERATIONS } = options;
  if (!passphrase) {
    return { keyBytes, kdf: { name: 'raw' } };
  }
  const passphraseKey = await derivePbkdf2KeyBytes(passphrase, salt, iterations);
  return {
    keyBytes: xorBytes(passphraseKey, keyBytes),
    kdf: {
      name: 'PBKDF2',
      hash: 'SHA-256',
      iterations,
      combiner: 'xor',
    },
  };
}

export async function encryptPasteText(options: {
  text: string;
  keyBytes: Uint8Array;
  iv: Uint8Array;
  salt: Uint8Array;
  passphrase?: string;
  contentType?: string;
}): Promise<PasteCipher> {
  const plaintextBytes = utf8ToBytes(options.text);
  const { keyBytes, kdf } = await derivePasteKeyBytes({
    keyBytes: options.keyBytes,
    passphrase: options.passphrase,
    salt: options.salt,
  });
  const ciphertextBytes = await encryptAesGcm(keyBytes, options.iv, plaintextBytes);
  return {
    version: 1,
    ciphertext: bytesToBase64Url(ciphertextBytes),
    iv: bytesToBase64Url(options.iv),
    salt: bytesToBase64Url(options.salt),
    kdf,
    alg: 'AES-GCM',
    contentType: options.contentType ?? 'text/plain; charset=utf-8',
    size: plaintextBytes.length,
  };
}

export async function decryptPasteText(options: {
  cipher: PasteCipher;
  keyBytes: Uint8Array;
  passphrase?: string;
}): Promise<string> {
  const iv = base64UrlToBytes(options.cipher.iv);
  const salt = base64UrlToBytes(options.cipher.salt);
  const ciphertext = base64UrlToBytes(options.cipher.ciphertext);
  const passphrase =
    options.cipher.kdf.name === 'PBKDF2' ? options.passphrase : undefined;
  const iterations =
    options.cipher.kdf.name === 'PBKDF2' ? options.cipher.kdf.iterations : undefined;
  const { keyBytes } = await derivePasteKeyBytes({
    keyBytes: options.keyBytes,
    passphrase,
    salt,
    iterations,
  });
  const plaintext = await decryptAesGcm(keyBytes, iv, ciphertext);
  return bytesToUtf8(plaintext);
}
